/* eslint-disable no-param-reassign */
import Vue from 'vue';
import Vuex from 'vuex';
import feathersVuex from 'feathers-vuex';
import sift from 'sift';
import feathersClient from '../feathers-client';

const {
  service,
  auth,
  FeathersVuex,
} = feathersVuex(feathersClient, {
  idField: '_id',
});
const year = (new Date()).getFullYear();
const startyear = new Date(`${year}-01-01`);
const midyear = new Date(`${year}-06-30`);
const finishyear = new Date(`${year}-12-31`);
const period = Date.now() < midyear.getTime() ? 1 : 2;

Vue.use(Vuex);
Vue.use(FeathersVuex);

const store = new Vuex.Store({
  plugins: [
    service('api/attempts', {
      instanceDefaults: {
        studentId: '',
        supervisorId: '',
        locId: '',
        unitId: '',
        proIds: [],
        stepIds: [],
        comments: {
          student: '',
          staff: '',
          staffPrivate: '',
        },
        duration: 90,
        grades: [],
        teeth: [],
        patient: '',
        patientDOB: new Date(),
        stat: 'inprogress',
        version: 4,
      },
      state: {
        daysLoaded: {},
        slowPercent: 1,
        slowCount: 0,
        slowError: '',
      },
      mutations: {
        addDaysLoaded(state, v) {
          Object.assign(state.daysLoaded, v);
        },
        setPercent(state, v) {
          state.slowPercent = v;
        },
        setCount(state, v) {
          state.slowCount = v;
        },
        setSlowError(state, v) {
          state.slowError = v;
        },
      },
      actions: {
        async slowFind({ dispatch, commit, state }, { query, limit }) {
          commit('setSlowError', '');
          if (!query.time || !query.time.$gte || !query.time.$lte) {
            commit('setSlowError', 'Time range required');
            throw new Error(state.slowError);
          }
          commit('setCount', (await dispatch('find', {
            query: { ...query, $limit: 0 },
            paginate: true,
          })).total);
          if (limit && state.slowCount > limit) {
            commit('setSlowError', `Request of ${state.slowCount} attempts exceeds limit of ${limit},
            change the date range to reduce the sample size.`);
            throw new Error(state.slowError);
          }
          const flags = store.getters['flags/find']().data.map((flag) => {
            if (flag.query) return flag;
            flag.query = JSON.parse(flag.match);
            if (!Object.keys(flag.query).filter(i => i !== 'stat').length) flag.query.foo = 'bar';
            if (!flag.retro) flag.query.updatedAt = { $gte: flag.createdAt };
            flag.nonManualQuery = flag.query;
            if (flag.manual) flag.query = { $or: [flag.query, { flagIds: flag._id }] };
            flag.filter = sift(flag.query);
            return flag;
          });
          console.log(flags);
          const days = [];
          const lte = new Date(query.time.$lte);
          const gte = new Date(query.time.$gte);
          const t = new Date(Math.min(lte.getTime(), Date.now()));
          const today = (new Date()).toISOString().substr(0, 10);
          while (t.getTime() >= gte.getTime()) {
            const day = t.toISOString().substr(0, 10);
            if ((state.daysLoaded[day] || 0) < Date.now() - 3 * 60 * 60 * 1000 || day === today) {
              days.push(day);
            }
            t.setTime(t.getTime() - 24 * 60 * 60 * 1000);
          }
          commit('setPercent', 0);
          const loadDay = async (i) => {
            const day = days[i];
            if (!day) return;
            const atts = (await dispatch('find', {
              query: {
                ...query,
                time: {
                  $gte: new Date(`${day}T00:00:00`),
                  $lte: new Date(`${day}T23:59:59`),
                },
                $client: {
                  populate: false,
                },
                $limit: 1000,
              },
            })).data;
            atts.forEach((att) => {
              att.slowFound = true;
              att.unit = (store.getters['units/get'](att.unitId) || {}).subname;
              const student = store.getters['users/get'](att.studentId) || {};
              att.student = student.text;
              att.student_username = student.username;
              att.student_firstname = student.profile.firstname;
              att.student_lastname = student.profile.lastname;
              att.supervisor = (store.getters['users/get'](att.supervisorId) || {}).text;
              att.location = (store.getters['locations/get'](att.locId) || {}).text;
              att.procedures = store.getters['procedures/find']({
                query: { _id: { $in: att.proIds } },
              }).data.map(p => p.text);
              att.steps = store.getters['steps/find']({
                query: { _id: { $in: att.stepIds } },
              }).data.map(p => p.text);
              att.flags = [];
              att.flagIds = att.flagIds || [];
            });
            flags.forEach(flag => atts.filter(flag.filter).forEach((att) => {
              att.flags.push(flag);
              if (!att.flagIds.includes(flag._id)) att.flagIds.push(flag._id);
            }));
            const obj = {};
            obj[day] = Date.now();
            commit('addDaysLoaded', obj);
            commit('setPercent', (i + 1) / days.length);
            await loadDay(i + 1);
          };
          await loadDay(0);
          commit('setPercent', 1);
        },
      },
    }),
    service('api/flags', {
      instanceDefaults: {
        name: '',
        desc: '',
        notify: [],
        visibleTo: 'student',
        colour: '#6791bf',
        retro: true,
        manual: false,
        match: '{}',
      },
    }),
    service('api/heuristics', {
      instanceDefaults: {
        text: '',
      },
    }),
    service('api/issues', {
      instanceDefaults: {
        title: '',
        body: '',
        labels: [],
      },
    }),
    service('api/locations', {
      instanceDefaults: {
        name: '',
        desc: '',
      },
    }),
    service('api/patients'),
    service('api/procedures', {
      instanceDefaults: {
        name: '',
        desc: '',
        stepIds: [],
        teeth: [],
        nonProcedural: false,
        adminOnly: false,
        patientInfo: false,
      },
    }),
    service('api/steps', {
      instanceDefaults: {
        name: '',
        desc: '',
        category: 'v3-Normal_5_Options',
        defaultChecked: false,
        inputDisabled: false,
      },
    }),
    service('api/units', {
      instanceDefaults: {
        name: '',
        unitcode: '',
        period,
        year,
        start: period === 1 ? startyear : midyear,
        finish: period === 1 ? midyear : finishyear,
        enrollment: [],
      },
    }),
    service('api/users', {
      instanceDefaults: {
        username: '',
        email: '',
        profile: {
          firstname: '',
          lastname: '',
        },
        perm: 'student',
      },
    }),
    auth({
      userService: 'users',
    }),
  ],
});

feathersClient.service('api/attempts').timeout = 60000;
feathersClient.service('api/flags').timeout = 30000;
feathersClient.service('api/heuristics').timeout = 30000;
feathersClient.service('api/issues').timeout = 30000;
feathersClient.service('api/locations').timeout = 30000;
feathersClient.service('api/patients').timeout = 30000;
feathersClient.service('api/procedures').timeout = 30000;
feathersClient.service('api/steps').timeout = 30000;
feathersClient.service('api/units').timeout = 30000;
feathersClient.service('api/users').timeout = 30000;

export default store;
